import * as React from 'react'
import { useContext } from 'react'
import styled, { ThemeContext } from 'styled-components'
import { Colors } from '../../constants/colors'
import { BaseIconProps } from '../icons/types'
import Plus from '../icons/svg/Plus'

type Coords = {
  left?: number
  top?: number
}

type IllustrationProps = {
  /*
  Width of the illustration
   */
  size?: number
  /*
  Icon that goes in the background of the illustration
   */
  backgroundIcon?: 'phone' | 'glass'
  /*
  Icon that goes within the magnifying glass
   */
  icon?: ({ color }: { color?: Colors; size?: number }) => JSX.Element
  /*
  Size of the icon that goes within the magnifying glass
   */
  iconSize?: number
  /*
  Position of the icon that goes within the magnifying glass
   */
  iconPosition?: Coords
}
const ratio = 268 / 399

const IconBg = styled.div`
  position: absolute;
  top: 0px;
  width: 100%;
  text-align: center;
`

const Holder = styled.div`
  position: relative;
  width: max-content;
  > svg {
    position: relative;
  }
`

const defaultSize = 399

function PaintbrushIcon({
  size = defaultSize,
  icon = Plus,
  iconSize,
  backgroundIcon = 'phone',
  iconPosition
}: IllustrationProps) {
  const themeContext = useContext(ThemeContext)
  return (
    <Holder>
      <IconBg style={{ top: size / 16 }}>
        {backgroundIcon === 'phone' ? (
          <Phone color={themeContext?.palette?.illustrations[50] || '#7549EA'} size={size} />
        ) : (
          <Glass
            color={themeContext?.palette?.illustrations[50] || '#7549EA'}
            icon={icon}
            size={size}
            iconSize={iconSize}
            iconPosition={iconPosition}
          />
        )}
      </IconBg>
      <svg width={size} height={size * ratio} viewBox="0 0 399 268" fill="none">
        <path
          d="M143.442 97.94c.908-.794 1.249-2.156 1.476-3.292.113-.567.227-1.021.454-1.475.113-.341.227-.682.34-1.136 0-.113.001-.227.114-.227 0 0 0 .114-.114.114 0 0 .001-.114.114-.114.114-.227.227-.34.341-.568.454-.68.227-1.703-.568-2.043-.681-.34-1.589-.227-2.044.567-.454.795-.794 1.59-1.021 2.385-.114.567-.341 1.022-.455 1.59l-.34 1.362c0 .113-.114.227-.114.34-.113.227 0-.113.114-.113 0 .113-.114.113-.114.227 0 .113-.227.34-.113.113l-.114.114c-.567.454-.567 1.59 0 2.157.568.567 1.363.454 2.044 0z"
          fill="#F4C5AE"
        />
        <path
          d="M140.831 99.303c-4.655 4.655-13.851 12.943-22.366 9.878-8.629-3.066-18.052-7.834-18.052-7.834l-1.816-8.515"
          fill={themeContext?.palette?.illustrations[50] || '#7549EA'}
        />
        <path
          d="M161.721 78.3c-3.406 4.2-9.878 4.314-13.397.227l-.341-.34c-.113-.114-.227-.341-.34-.455-3.179-4.314-1.476-10.558 3.406-12.943 6.017-2.952 18.505-8.515 20.889-5.45 2.385 2.953-6.017 13.738-10.217 18.96z"
          fill="#FFBC80"
        />
        <path
          d="M158.768 60.362c-1.475 1.021-1.248 2.951-.227 3.973-.113.227-.113.454-.113.681-.114 1.363.113 2.498.908 3.633.454.568.908 1.136 1.476 1.477.454.34.681.68.795.567.227 2.044-2.044 4.2-.114 6.13 1.817 1.817 3.747 0 4.769-1.589.794-1.248 1.362-2.724 2.157-3.973.908-1.59 2.27-2.725 3.292-4.2 1.476-2.045 2.952-5.223 3.179-7.721.227-7.607-13.397-.908-16.122 1.022z"
          fill={themeContext?.palette?.illustrations[50] || '#7549EA'}
        />
        <path
          d="M91.217 81.479c-.568 2.043-.909 4.2-.795 6.358 0 1.021.795 1.93 1.817 1.816.908 0 1.93-.795 1.816-1.816-.114-1.136 0-2.157.114-3.293 0 .114 0 .34-.114.454.114-.908.34-1.703.568-2.611.227-.908-.34-2.043-1.25-2.27-.794-.114-1.816.34-2.156 1.362z"
          fill="#542316"
        />
        <path d="M80.658 87.269l7.266 6.13 4.427-2.043.114-7.72-7.493-6.585-4.314 10.218z" fill="#F4C5AE" />
        <path
          d="M58.633 239.516c1.022 3.406-1.249 12.375-3.065 14.645-1.476 1.817 13.17 1.817 13.17 1.817s-3.293-7.38-.795-19.755"
          fill={themeContext?.palette?.illustrations[40] || '#806DDB'}
        />
        <path
          d="M59.428 244.284l-2.498 12.943M60.79 243.83l-1.135 13.51M62.494 243.83l-.454 14.305M64.196 244.397l.568 14.306M65.899 243.603l1.249 15.44"
          stroke={themeContext?.palette?.illustrations[60] || '#5E3ABB'}
          strokeWidth={0.46}
          strokeMiterlimit={10}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M51.934 264.492l8.288 1.249.34-1.93c2.044.454 3.52 1.59 4.996 2.498.567.341 1.476.454 1.703.568l16.235.227c.681.113 1.362-.227 1.703-.909.113-.227.34-.908.34-.908l-.68-.113-32.13-4.882-.114.567-.681 3.633z"
          fill={themeContext?.palette?.illustrations[70] || '#462C8C'}
        />
        <path
          d="M67.375 252.231c.908-.34 1.816.114 2.27.908 3.293 5.791 12.375 6.699 14.532 8.969.909 1.022 1.363 1.703 1.363 2.952 0 .568-.454.909-1.022.909-4.2 0-15.781.454-18.733-.909-1.022-.454-2.725-1.362-3.746-1.816-2.498-1.136-9.537-2.498-9.537-2.498s.908-5.563 2.725-7.834c.794-1.021 2.384.682 6.017.795 1.362 0 4.314-.908 6.13-1.476z"
          fill={themeContext?.palette?.illustrations[80] || '#2F1D5E'}
        />
        <path
          d="M86.108 263.357c0 .568-.454 1.476-1.362 1.476-3.18 0-15.1.681-18.733-1.135-1.022-.454-2.725-1.363-3.747-1.817-2.497-1.135-9.536-2.497-9.536-2.497"
          stroke={themeContext?.palette?.illustrations[30] || '#AC92F2'}
          strokeWidth={0.406}
          strokeMiterlimit={10}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M72.028 256.091c-.794.795-2.61 2.612-3.405 1.817-.795-.908.34-2.157 1.93-3.293M75.321 258.021c-.454.682-2.043 3.293-3.178 2.157-1.022-1.021.794-2.611 1.362-3.292M79.068 259.838c-.568 1.022-1.59 3.179-2.952 2.27-1.135-.681.454-2.384 1.022-3.406"
          stroke={themeContext?.palette?.illustrations[60] || '#5E3ABB'}
          strokeWidth={0.406}
          strokeMiterlimit={10}
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeDasharray="0.9 0.9"
        />
        <path
          d="M96.78 239.629c1.589 3.179.567 10.786 0 14.532-.341 2.271 13.282-.113 13.282-.113s-4.768-5.109-4.314-16.576"
          fill={themeContext?.palette?.illustrations[40] || '#806DDB'}
        />
        <path
          d="M98.596 244.625l-.568 13.283M99.959 243.944l.908 13.51M101.661 243.717l1.703 14.191M103.364 243.944l2.725 14.191M104.954 242.922l3.52 15.1"
          stroke={themeContext?.palette?.illustrations[60] || '#5E3ABB'}
          strokeWidth={0.46}
          strokeMiterlimit={10}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M94.85 265.855h8.287v-2.043c2.157.113 3.747 1.021 5.336 1.703.681.34 1.476.227 1.703.227h16.462c.681 0 1.249-.454 1.476-1.136.114-.227.114-.908.114-.908h-.682l-32.81-2.271v.568l.113 3.86z"
          fill={themeContext?.palette?.illustrations[70] || '#462C8C'}
        />
        <path
          d="M108.247 251.323c.794-.454 1.816-.114 2.384.568 4.201 5.222 13.17 6.812 15.554 8.628 1.135.795 1.93 1.817 2.157 2.952.113.568-.341 1.022-.908 1.022-3.179.227-15.1.908-18.96-.341-1.022-.34-2.952-.908-3.974-1.249-2.611-.681-9.764-1.022-9.764-1.022s0-5.563 1.476-8.174c.568-1.022 2.384.227 6.131-.113 1.362 0 4.201-1.363 5.904-2.271z"
          fill={themeContext?.palette?.illustrations[80] || '#2F1D5E'}
        />
        <path
          d="M128.341 262.222c.114.568-.34 1.022-.908 1.022-3.179.227-15.1.908-18.96-.341-1.022-.34-2.952-.908-3.974-1.249-2.611-.681-9.763-1.021-9.763-1.021"
          stroke={themeContext?.palette?.illustrations[30] || '#AC92F2'}
          strokeWidth={0.406}
          strokeMiterlimit={10}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M113.468 254.502c-.568.908-2.157 2.952-3.066 2.271-.908-.795 0-2.157 1.476-3.633M116.875 256.545c-.341.795-1.476 3.293-2.725 2.384-1.135-.794.454-2.611.795-3.406M120.62 258.135c-.34 1.022-1.021 3.406-2.611 2.725-1.249-.568.114-2.385.568-3.52"
          stroke={themeContext?.palette?.illustrations[60] || '#5E3ABB'}
          strokeWidth={0.406}
          strokeMiterlimit={10}
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeDasharray="0.9 0.9"
        />
        <path
          d="M81.907 87.836c4.882-.34 9.65.228 13.965.455 5.563.34 17.257 4.654 28.837 11.92"
          fill={themeContext?.palette?.illustrations[50] || '#7549EA'}
        />
        <path
          d="M94.623 90.788c5.109.34 7.833-.794 9.196 2.157 1.816 3.86.227 11.921.227 16.122l.113 32.13-34.968-.114 1.25-33.946 6.47-10.218"
          fill={themeContext?.palette?.illustrations[50] || '#7549EA'}
        />
        <path
          d="M86.562 172.986l4.088 23.501 5.449 52.111 14.419-.794v-47.343c0-.682 0-1.476-.114-2.157l-6.244-57.107-34.968-.114-3.406 10.786-1.93 42.347-8.061 53.815 14.986.794 11.808-49.5c.113-.681.227-1.249.34-1.93l3.633-24.409z"
          fill={themeContext?.palette?.illustrations[70] || '#462C8C'}
        />
        <path
          d="M103.819 100.325l-.454-4.428"
          stroke="#323232"
          strokeWidth={0.46}
          strokeMiterlimit={10}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M71.348 157.886c-5.676 4.428-14.418 2.725-19.64-3.747-5.223-6.585-4.77-15.44.794-19.981l88.782-59.264c6.812-4.541 14.192 4.882 8.175 10.331l-78.11 72.661z"
          fill={themeContext?.palette?.illustrations[60] || '#5E3AAB'}
        />
        <path
          d="M71.12 116.674l7.72 3.519v10.445l-12.147-6.471M83.155 124.621c2.838 1.135 5.677 2.611 8.06 4.428.682.454 1.704.113 2.044-.568.454-.795.114-1.589-.567-2.044-2.385-1.816-5.223-3.292-8.061-4.427-.681-.341-1.703-.227-2.044.567-.227.568-.113 1.703.568 2.044z"
          fill="#F4C5AE"
        />
        <path
          d="M81.11 126.438c3.52 1.93 7.04 3.633 10.332 5.79.681.454 1.703.113 2.044-.568.454-.795.113-1.589-.568-2.044-3.293-2.157-6.812-3.973-10.332-5.79-.68-.34-1.702-.227-2.043.568-.34.568-.114 1.589.568 2.044z"
          fill="#F4C5AE"
        />
        <path
          d="M81.225 129.503l1.022.34c.227.114.34.114.568.227 0 0 .34.114.113.114-.227-.114.114.113.114.113.794.341 1.476.795 2.27 1.136a47.843 47.843 0 013.974 2.611c.681.454 1.703.113 2.044-.568.454-.795.113-1.589-.568-2.043-1.249-1.022-2.725-1.93-4.087-2.725-1.476-.795-3.066-1.59-4.769-2.044-.567-.34-1.476.114-1.703.908-.113.795.227 1.703 1.022 1.931z"
          fill="#F4C5AE"
        />
        <path
          d="M78.386 129.957c2.725 1.703 5.45 3.292 8.174 5.109.681.454 1.703.113 2.044-.568.454-.794.113-1.589-.568-2.043-2.725-1.817-5.45-3.406-8.174-5.109-.681-.454-1.703-.227-2.044.567-.34.682-.113 1.59.568 2.044z"
          fill="#F4C5AE"
        />
        <path
          d="M82.588 123.145h.227c.454.113.794.113 1.135-.114.34-.227.568-.567.681-.908.114-.341.114-.795-.113-1.135-.228-.341-.568-.681-.909-.681-.34 0-.68-.114-1.021 0-.227.113-.341.113-.568.227-.454.113-.908.113-1.362.454-1.022.567-1.363 1.816-1.476 2.838 0 .227-.114.454-.114.681 0-.454 0-.113 0 0 0 .114-.114.227-.114.341.114-.227.114-.227 0-.114l-.113.114c0-.114.114-.114.114-.227l-.114.113-.34.341c-.682.568-1.477 1.362-1.363 2.498.113.794.568 1.362 1.362 1.476.795.113 1.363-.341 1.703-1.136.227-.681.341-1.249.454-1.93.114-.681.114-1.476-.227-2.043-1.135-1.817-3.86-.341-4.087 1.362-.113.454-.113.795.114 1.135.227.341.567.568.908.681.34.114.795.114 1.135-.113.341-.227.682-.568.682-.908v-.114c0-.113 0-.113-.114.114 0 0 0-.114.114-.114-.114.227-.228.227-.114.114l-.114.113s.114 0 .114-.113c-.227.113-.34.113-.114.113-.227.114-.567 0-.794-.113l-.568-.568c-.113-.114-.113-.341-.113-.454v.795c0 .34.113-.341 0 0l-.341 1.362c1.022.114 2.044.227 2.952.454v-.227c0 .341-.114.341 0 .227v-.113c.113.113.113.113.113-.114.114-.227-.227.227-.113.114.227-.227.454-.341.681-.568.681-.681 1.135-1.476 1.249-2.384 0-.227.113-.454.113-.681v-.228c0-.227-.113.455 0 .114v-.227c0-.114.114-.341 0-.114-.113.227 0 0 0-.113.114-.227-.113.113-.113.113s.227-.227 0 0c-.34.227.227-.113-.114.114-.227.113 0 0 .114 0h.227c.34-.114.568-.114.908-.227 0-.227-.227-.227-.567-.227h.227c.227-1.022.567-1.93.794-2.952h-.227c-.454-.114-.794-.114-1.135.114-.34.227-.568.567-.681.908-.114.34-.114.794.113 1.135.227.454.568.795.909.795z"
          fill="#F4C5AE"
        />
        <path
          d="M82.7 128.822c-1.248-.455-2.61-.795-3.973-1.249-.113 0-.227-.114-.454-.114-.114 0-.114-.113-.227-.113.34.113.113.113.113 0-.113 0-.227-.114-.34-.114 0 0-.114 0-.114-.113-.227-.114.227.113 0 0-.227-.114-.34-.341-.567-.568l-.227-.227c-.228-.227.113.113 0-.114-.114-.113-.114-.227-.228-.454-.113-.227 0 0 0 0 0-.113-.113-.227-.113-.34-.113-.227 0 .227 0-.114v-.454c0-.227-.113.341 0 .114v-.228c0-.113.114-.34.114-.454 0-.113.113-.34.113-.454 0-.113 0-.113.114-.227 0 0-.114.227 0 .114.113-.227-.228.113 0 0 .113-.114-.228.113-.114.113 0 0 .114-.113.227-.113.227-.114 0 0-.113 0 .113 0 .113 0 .227-.114.113 0 .34-.113.454-.113.34-.114.794-.227 1.135-.341a5.766 5.766 0 012.27-.454h1.477c.227 0 .454 0 .794.113h.114c.114 0 .227 0 .454.114l1.362.341c.228.113.341.113.568.227-.34-.114.227.113.34.113.682.341 1.704.227 2.044-.568.454-.681.227-1.703-.567-2.043-2.044-1.135-4.201-1.476-6.245-1.476-1.021 0-2.157.113-3.178.454-.909.341-1.817.568-2.612.908-1.59.682-2.043 2.612-2.043 4.201 0 1.93 1.476 3.86 3.178 4.655 1.703.795 3.52 1.249 5.223 1.816.795.227 1.703-.34 1.816-1.021.228-.682-.226-1.476-1.021-1.703z"
          fill="#F4C5AE"
        />
        <path
          d="M81.906 122.918s.114.227 0 0c0-.114.114.113.114.113l.34.341c.227.227.34.568.568.795l.34.681c.114.113.114.227.228.454-.114-.227 0 .114.113.114.114.227.114.567.227.794 0 .114.114.341.114.341 0-.114 0-.227 0 0v.681c0 .227-.114.454-.227.795-.114.227 0 0 0 0s-.114.113-.114.227v.113c.114-.113.34-.227.454-.454l.34-.113h-.226c-.227 0-.454.113-.795.113h-.227.114c-.228 0-.341 0-.568.114-.34 0-.795.113-1.135.113h-1.022c-.227 0 .34.114.113 0h-.113c-.114 0-.34-.113 0 0 .227.114.113 0 0 0 .113 0 .34.341 0 0 .113 0-.454-.34-.227-.113.113.113-.114-.114-.227-.114-.114-.113-.228-.113-.228-.227l-.113-.113c.114.113.114.227.114.113v-.113c-.114-.114 0 .113 0 .113s0-.113-.114-.227c0-.113-.114-.34-.114-.454-.113-.341-.227-.568-.34-.908-.114-.341-.227-.681-.227-.908 0-.114-.114-.228-.114-.455v-.227c0-.113 0-.113 0 0v-.908-.113c0-.228.114-.341.114-.568v.113l.113-.113c.114-.114 0 0 0 .113-.113 0-.113.114-.227.114h-.113.454c.227 0-.227 0 0 0 .227.113.567.113.795.227.113 0 .34.114.113 0 .114.114.34.114.454.227.114.114.227.114.34.227l.114.114c.34.227-.113-.114 0 0l.681.681.114.113.114.114c-.114-.114 0 .113 0 .113 0 .114.113.114.113.228.114.113.227.34.34.454 0 0 .114.227 0 .113 0-.113 0-.113 0 0 0 .114 0 .114 0 0v-.34c.114-.227-.113.113 0-.114.114-.113 0 0 0 0 .114-.113.228-.113 0 0-.113 0-.113.114-.226.114-.227.113 0 0 .113 0l-.34.113c-.682.227-1.476.454-2.044 1.135.454-.113 1.022-.227 1.476-.34.681.113.795 1.249.681.454 0-.114 0-.227-.113-.341 0-.227-.114-.454-.114-.794l-.34-1.363c-.228-.908-.455-1.93-.568-2.838-1.022.227-1.93.568-2.952.795a7.43 7.43 0 001.476 2.611c.568.681 1.135 1.135 1.703 1.816.227-.794.454-1.703.681-2.497-.908.227-1.93.454-2.952.681.568.795 1.136 1.476 1.703 2.271 0-.114.114-.114.114-.227.113-.114-.227.227 0 0l.113-.114.114-.114.227-.113c-.227.113-.227.113-.114 0h.114l.227-.114h-.227.227c.795 0 1.59-.681 1.476-1.476 0-.794-.681-1.589-1.476-1.475-1.476 0-2.838.794-3.52 2.043-.34.568-.227 1.249.114 1.703.454.568 1.022.681 1.59.568 1.021-.114 1.93-.341 2.951-.681 1.136-.341 1.363-1.703.681-2.498-.454-.568-1.021-1.135-1.589-1.703-.114-.114-.227-.341-.34-.341.113.114.113.227.113.114 0 0 0-.114-.114-.114 0-.113-.113-.113-.113-.227-.114-.113-.227-.34-.227-.454-.114-.113 0-.113 0 0 0-.113-.114-.227-.114-.34-.227-.795-1.022-1.363-1.816-1.022a1.458 1.458 0 00-1.022 1.816c.34 1.59.681 3.293 1.022 4.882.113.568.227 1.022.567 1.476.568.681 1.817.795 2.498 0 .227-.227-.227.114 0 0 .114 0 .114-.113.227-.113 0 0-.34.113 0 0 .114 0 .114 0 .227-.114.454-.114.909-.227 1.25-.454 1.02-.568 1.702-1.817 1.361-2.952-.227-1.022-1.021-1.817-1.702-2.611-1.25-1.363-3.18-2.384-4.996-2.384-.681 0-1.249.227-1.816.681-.454.34-.682.908-.909 1.476-.34 1.021-.34 2.043-.113 3.065.113.908.454 1.703.681 2.498.114.454.227.908.568 1.249.227.454.567.681 1.021 1.021.114.114.227.227.455.341.227.227 0 0 0 0 .227.114.227.227.454.341.227.113.567.227.794.227.568.113 1.136.113 1.703.113 1.022 0 2.044-.113 3.066-.34.794-.114 1.249-.568 1.476-1.249.454-.908.68-1.93.567-2.952-.227-2.157-1.362-3.974-2.724-5.677-.454-.681-1.59-.567-2.157 0-.228.568-.228 1.476.34 2.044z"
          fill="#F4C5AE"
        />
        <path
          d="M92.578 129.162c-1.59-1.476-3.86-2.838-5.903-3.746M91.556 132.114c-1.816-1.816-3.519-3.179-6.244-4.541M88.83 133.59c-1.816-1.817-3.519-2.838-5.222-3.747M87.696 123.259c-1.135-.682-2.952-1.249-3.974-1.363"
          stroke="#323232"
          strokeWidth={0.535}
          strokeMiterlimit={10}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M75.095 104.98l-14.646 6.698 18.392 7.38-4.314 9.536s-9.764-3.746-21.798-7.947c-9.196-3.292-8.742-14.418-.454-18.846 17.938-9.65 25.772-14.078 32.924-13.965 6.472 0 7.607.682 12.489.909"
          fill={themeContext?.palette?.illustrations[50] || '#7549EA'}
        />
        <path
          transform="rotate(-133.114 48.987 142.176)"
          fill="url(#prefix__pattern0)"
          d="M48.987 142.176h64.228v64.228H48.987z"
        />
        <path
          d="M70.44 107.137l3.406-1.589M76.57 117.923l-6.13-2.498"
          stroke="#323232"
          strokeWidth={0.46}
          strokeMiterlimit={10}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M134.587 98.622c.34.454 1.021.681 1.589.681.341 0 .681-.113 1.022-.227.113-.113.454-.227.454-.34.113 0 0 0 0 0-.227.227-.454.227-.795 0-.794-.114-.794-.228-.567.113.113 0 .113.114.227.114.227.113.34.34.567.454.568.34 1.249.34 1.817.113.34-.113.681-.454.908-.68l.114-.114c-.455.794-.795.567-1.249.454-.114-.114-.114 0 0 0l.113.113c.114 0 .114.114.227.114.341.113.682.34 1.022.227.568 0 1.135-.227 1.476-.681.227-.114.341-.34.454-.568l.114-.114v-.113c0-.114 0-.114-.114.113l-.681.341h-.454c.113 0 .34 0 .454.114.227 0 .454 0 .681-.114a2.71 2.71 0 001.249-.681c.795-.681.908-1.817.454-2.725-.34-.681-1.362-1.022-2.043-.568-.682.455-1.022 1.363-.568 2.044-.114-.227-.114-.454-.114-.681 0-.114.114-.114.114-.227-.114.113-.114.113 0 0 0 0 .341-.227.113-.114.114 0 .114 0 .228-.113-.114 0-.114 0 0 0h-.114.227c-.113 0-.341 0-.454-.114-.454 0-.908.114-1.249.454-.227.227-.341.34-.454.568 0 .114-.114.227-.114.227l.114-.113c-.114.113-.114.113 0 0 .227-.114.341-.228.568-.228.113 0 .681.228.34.114-.227-.114-.454-.34-.681-.34-.681-.228-1.476 0-1.93.454 0 0-.341.34-.341.454.227-.114.455-.341.682-.454h.34c.114 0 .454.227.227 0 .341.227.114.113 0 0-.567-.568-1.135-.909-1.93-.682a3.41 3.41 0 00-.908.454c-.114.114-.114.114 0 0a.867.867 0 01.568-.227h.227c.454.34.567.34.34.227-.567-.567-1.589-.567-2.157 0-.681.795-.681 1.703-.113 2.271z"
          fill="#F4C5AE"
        />
        <path
          d="M141.512 97.033c-.681-2.384-1.249-4.769-1.93-7.04-.113-.34-.227-.68-.227-1.021-.227-.795-1.135-1.362-1.817-1.022a1.458 1.458 0 00-1.021 1.817c.681 2.384 1.249 4.768 1.93 7.039.113.34.227.68.227 1.022.227.794 1.135 1.362 1.816 1.021a1.458 1.458 0 001.022-1.816z"
          fill="#F4C5AE"
        />
        <path
          d="M138.9 97.146c-.454-2.043-.908-3.973-1.362-6.017-.114-.227-.114-.568-.227-.795-.227-.794-1.136-1.362-1.817-1.022a1.458 1.458 0 00-1.022 1.817c.455 2.044.909 3.974 1.363 6.017.113.227.113.568.227.795.227.795 1.135 1.362 1.816 1.022.682-.227 1.136-.908 1.022-1.817z"
          fill="#F4C5AE"
        />
        <path
          d="M133.224 92.605c.114 1.816.341 3.633.795 5.336.113.568.34 1.135.681 1.476.341.34.795.568 1.135.568.909.227 1.817.113 2.612-.114.794-.114 1.589-.34 2.27-.681.681-.227 1.363-.454 1.93-.908.341-.227.681-.454.795-.795.341-.568.227-1.022.113-1.59-.113-.68-.454-1.476-.681-2.157-.454-1.362-1.021-2.724-1.589-4.087-.341-.681-1.022-1.362-1.817-1.022-.681.227-1.362 1.136-1.021 1.817.454 1.022.794 1.93 1.135 2.952.227.794.568 1.476.795 2.27.113.341.113.568.227.909v-.114c0-.34.227-.681.34-.795-.113.227.454-.227.114-.113-.114 0-.227.113-.227.113-.114.114-.227.114-.341.114-.113 0-.113.113-.227.113-.341.114.227-.113.114 0l-2.044.682c-.341.113-.681.227-1.022.227h-1.022c.114 0 .455.113.227 0h-.227c-.113 0 .341.227.227.113-.227-.113.341.227.114.114-.114-.114.227.34.114.227 0-.114.113.34.113.227v-.227l-.341-1.703c0-.34-.113-.568-.113-.908 0-.114 0-.341-.114-.454v-.114c-.113-.681-.113-1.249-.227-1.93-.113-.795-.681-1.59-1.476-1.476-.681.454-1.475 1.022-1.362 1.93z"
          fill="#F4C5AE"
        />
        <path
          d="M139.354 89.085l1.703 6.585M136.517 89.54l1.816 7.152M134.587 91.129s.794 3.746 1.475 6.244"
          stroke="#323232"
          strokeWidth={0.535}
          strokeMiterlimit={10}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M92.125 158l-3.52 2.384-2.044 12.602"
          stroke="#323232"
          strokeWidth={0.46}
          strokeMiterlimit={10}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          transform="matrix(.0082 -.0458 -.0458 -.0082 113.831 216.283)"
          fill="url(#prefix__pattern1)"
          d="M0 0h1226.15v1226.15H0z"
        />
        <path
          transform="matrix(.0176 .04306 .04306 -.0176 .731 166.008)"
          fill="url(#prefix__pattern2)"
          d="M0 0h1226.15v1226.15H0z"
        />
        <path
          d="M77.706 88.177c0-3.86.568-7.039 1.135-10.672.114-.908-.227-2.043-1.249-2.27-.908-.228-2.157.227-2.27 1.248-.568 3.86-1.25 7.72-1.25 11.694 0 1.022.91 1.93 1.817 1.817.909 0 1.817-.795 1.817-1.817z"
          fill="#542316"
        />
        <path
          d="M80.432 86.815v-1.25-.226c0-.227.113-.34.113-.454.114-.795.34-1.476.568-2.271.454-1.476 1.022-3.065 1.476-4.541.34-.909-.34-2.044-1.25-2.271-1.02-.227-1.93.34-2.27 1.249-.454 1.59-1.022 3.179-1.476 4.768-.454 1.59-.908 3.293-.908 4.996 0 1.021.795 1.93 1.817 1.816 1.021 0 1.93-.795 1.93-1.816zM95.531 60.475h.34c.342 0-.34-.114-.113 0 .227 0 .341.114.568.114l.227.113c-.227-.113-.34-.113-.227-.113 0 0 .114 0 .114.113.113.114.34.227.454.34.113 0 .113.114.227.114.227.114-.34-.227-.114 0 .114.114.34.227.454.454.114.114.227.227.341.455.454-.114.568 0 .568.113.227.34-.228-.34 0 0l.68 1.022c.114.113.114.227.228.34 0 0 .227.455.113.114-.113-.227 0 .113.114.113 0 .114.114.228.114.341.113.227.113.454.227.795 0 .113 0 .227.113.34 0 .228 0-.34 0 0 0 .455.114 1.022 0 1.476-.114 1.022.908 1.93 1.817 1.817a1.793 1.793 0 001.816-1.817c.341-4.54-2.952-9.99-7.947-9.99-1.022 0-1.93.908-1.817 1.816-.113 1.022.681 1.93 1.703 1.93z"
          fill="#542316"
        />
        <path
          d="M88.833 82.841l5.676.908c3.747.228 5.45-2.497 6.699-6.244.794-2.498.567-8.288.567-8.288.227-5.336-2.952-10.218-7.833-11.353-1.817-.454-4.769-.454-6.926 0-4.882 1.135-7.834 4.541-8.174 9.537 0 0-.114 4.087.227 5.45 1.703 6.13 4.087 9.082 9.764 9.99z"
          fill="#F4C5AE"
        />
        <path
          d="M84.745 75.12c-1.022.795-2.611.568-3.406-.567-.681-1.135-.567-2.611.568-3.406 1.022-.795 2.27-.568 2.952.568.68 1.022.227 3.746.227 3.746"
          fill="#BE854F"
        />
        <path
          d="M83.496 81.025c2.384 1.816 8.969 2.61 8.969 2.61"
          stroke="#E5AD98"
          strokeWidth={0.408}
          strokeMiterlimit={10}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M79.636 78.527c.454-4.428 1.817-8.856 3.747-12.943.794-1.703 1.816-3.292 3.633-4.087 1.703-.681 4.314-1.249 6.017-.568.908.455 2.043.228 2.498-.68.454-.795.227-2.158-.682-2.498-2.61-1.136-5.563-.909-8.287 0-2.612.794-4.542 2.497-5.79 4.882-2.725 4.881-4.201 10.331-4.883 15.894-.113 1.022.909 1.817 1.817 1.817 1.135.113 1.817-.795 1.93-1.817z"
          fill="#542316"
        />
        <path
          d="M93.147 57.978c-3.747 2.384-5.11 6.585-6.585 10.558-.681 2.157-1.817 3.86-2.952 5.79-1.135 2.044-2.157 4.201-1.817 6.585.454-.567.909-1.249 1.363-1.816-.34.113-.681.227-1.022.227.795.454 1.476.908 2.27 1.249-.226-1.363.795-2.839 1.363-3.974a84.587 84.587 0 002.27-4.541c1.59-3.066 3.066-6.131 4.655-9.196 1.022-2.157-2.157-3.974-3.178-1.93-1.703 3.406-3.52 6.925-5.223 10.331-.795 1.59-1.703 3.293-2.498 4.882-.794 1.703-1.362 3.406-1.021 5.336.227 1.022 1.362 1.59 2.27 1.249.34-.114.681-.227 1.022-.227.681-.227 1.476-1.022 1.362-1.817-.227-2.043.682-3.746 1.703-5.336 1.022-1.703 2.044-3.406 2.725-5.336 1.249-3.179 2.044-7.152 5.11-9.082 2.043-1.022.226-4.201-1.817-2.952z"
          fill="#542316"
        />
        <path
          d="M93.487 59.34c0-.227 0-.114 0 0s-.113.227-.113.34c-.114.228-.114.455-.227.795-.114.227-.114.454-.227.682-.114.34.113-.341 0 .113 0 .114-.114.227-.114.227-.34.795-.68 1.476-1.022 2.27-.34.682-.794 1.363-1.248 2.044-.114.227.227-.34 0 0l-.227.227a1.564 1.564 0 00-.341.455l-.454.454s-.227.227-.114.113c.227-.113-.113.114-.113.114-.795.567-1.25 1.703-.681 2.497.567.795 1.703 1.25 2.497.682 1.703-1.136 2.952-2.839 3.86-4.655.455-.795.795-1.59 1.136-2.498.567-1.249 1.022-2.384 1.135-3.86.114-1.022-.908-1.93-1.816-1.817-1.25 0-1.817.795-1.93 1.817z"
          fill="#542316"
        />
        <path
          d="M95.076 60.248c.114.34.114.568.227.909 0 .113.114.34.114.454.113.34 0-.454 0 0v.908c0 .34.113-.454 0 0 0 .114 0 .227-.114.454-.113.227-.113.568-.227.795-.113.34.227-.34 0 0-.113.113-.113.227-.227.34-.227.454-.568 1.022-.794 1.476-.455.795-.228 2.044.68 2.498.909.454 2.044.227 2.498-.681.682-1.135 1.363-2.384 1.703-3.633.34-1.59.114-3.066-.227-4.541-.227-.909-1.362-1.59-2.27-1.25-1.022.341-1.59 1.25-1.363 2.271z"
          fill="#542316"
        />
        <path
          d="M96.44 61.61c.113.114.226.455 0 0l.113.114c.113.114.227.34.34.454.227.34.341.681.568 1.022-.114-.114-.114-.34 0-.114 0 .114.114.114.114.227.113.228.113.341.113.568.114.34.227.795.227 1.135 0-.113 0-.34-.113-.454a7.607 7.607 0 010 2.271c0-.114 0-.34.113-.454v.113c-.113.568-.113.909.227 1.476.227.34.681.795 1.136.909.454.113 1.021.113 1.475-.227.341-.228.795-.682.909-1.136.227-1.476.227-3.065-.227-4.541-.454-1.476-1.136-2.838-2.157-3.974a2.095 2.095 0 00-1.363-.567c-.454 0-1.022.227-1.362.567-.454.34-.681.795-.681 1.363.113.454.227.908.567 1.248zM88.719 82.955c.34-3.065.68-6.244 1.135-9.196.454-2.838 2.157-5.336 2.838-8.061.568-2.27-3.065-3.292-3.633-1.022-.34 1.476-1.022 2.725-1.59 4.087a27.257 27.257 0 00-1.362 4.315c-.454 3.292-.794 6.584-1.248 9.877-.114 1.022.908 1.816 1.816 1.816 1.249.114 1.93-.794 2.044-1.816z"
          fill="#542316"
        />
        <path
          d="M86.562 86.701c-3.746-1.362-9.082-.227-9.082-.227l2.27-3.86c.681-1.249 2.27-1.703 3.52-1.022 1.476.795 3.292 1.704 4.882 2.725"
          fill="#CEAC00"
        />
        <path
          d="M96.894 57.296c-2.952-2.27-7.04-2.611-10.559-1.816-3.52.795-5.904 3.179-7.72 6.13-3.52 5.904-4.768 12.943-5.563 19.642-.34 2.27-.454 4.54-.681 6.698-.114 1.022.908 1.816 1.816 1.816 1.136 0 1.703-.908 1.817-1.816.567-4.314.908-8.515 1.703-12.716.567-2.951 1.362-5.903 2.61-8.742.91-2.27 2.272-4.995 4.429-6.47 2.384-1.59 7.152-1.931 9.536-.114 1.817 1.475 4.542-1.136 2.612-2.612z"
          fill="#542316"
        />
        <path
          d="M89.967 60.021c-4.087 1.363-6.244 5.223-7.38 9.196-1.021 3.747-1.362 8.175-4.54 10.9 1.021.454 2.157.908 3.178 1.362-.113-3.633.795-7.04 2.498-10.218.795-1.59 1.703-3.066 2.725-4.541.454-.682 1.022-1.476 1.476-2.157.34-.455.567-.795.908-1.25.113-.226.454-.794.681-.908-.34.114-.681.114-.908.227.454 0 1.249.455 1.816.682.682.227 1.476.567 2.157.794.909.341 2.044-.34 2.271-1.249.34-1.021-.34-1.93-1.249-2.27a125.883 125.883 0 01-3.633-1.362c-1.476-.568-2.611-.114-3.52 1.135-1.475 2.043-3.065 4.2-4.314 6.358-2.724 4.427-4.654 9.536-4.427 14.759.113 1.703 1.93 2.384 3.179 1.362 3.065-2.611 4.087-6.585 4.995-10.331.681-3.18 1.703-7.607 5.223-8.742 2.043-.909 1.135-4.541-1.136-3.747z"
          fill="#542316"
        />
        <path
          d="M84.064 73.418c0 3.292-.794 6.471-.794 9.764 0 1.022.908 1.93 1.816 1.816a1.793 1.793 0 001.817-1.816c0-3.293.794-6.472.794-9.764 0-1.022-.908-1.93-1.816-1.817a1.793 1.793 0 00-1.817 1.817z"
          fill="#542316"
        />
        <path
          d="M83.156 79.435a32.425 32.425 0 00-.68 6.585c0 1.022.907 1.93 1.816 1.817a1.793 1.793 0 001.816-1.817c0-1.362.114-2.611.227-3.86 0 .113 0 .34-.113.454.113-.795.227-1.476.34-2.27.227-.909-.227-2.044-1.249-2.271-.794-.114-1.93.34-2.157 1.362z"
          fill="#542316"
        />
        <path
          d="M82.361 79.662c-.681 2.157-1.249 4.428-1.59 6.699-.113.454-.113 1.021.228 1.475.227.341.68.795 1.135.909.908.227 2.157-.227 2.27-1.25.341-2.27.909-4.54 1.59-6.698.227-.908-.34-2.043-1.249-2.27-1.135-.34-2.043.113-2.384 1.135z"
          fill="#542316"
        />
        <path
          d="M80.998 79.322c-1.249 2.611-1.816 5.336-2.27 8.174-.114.454-.114 1.022.227 1.476.227.34.68.795 1.135.908.908.227 2.157-.227 2.27-1.249.228-1.249.455-2.61.795-3.86.114-.68.34-1.249.568-1.816.113-.34.227-.568.34-.909 0-.113 0-.113.114-.227-.114.227-.114.227 0 .114.113-.114.113-.34.227-.454.454-.908.227-2.044-.681-2.498-1.022-.681-2.27-.567-2.725.34z"
          fill="#542316"
        />
        <path
          d="M78.615 80.684c-1.136 2.157-1.703 4.655-2.385 6.926.909-.34 1.817-.795 2.725-1.136-.34-.227-.681-.34-1.022-.567-.34-.227.34.34.114 0l-.227-.227c.227.227.227.34.113.227v-.114c.114.34.114.454.114.227v-.113c0 .113 0 .34.113.454v-1.135c0 .113 0 .34-.113.454 0-.34.113-.795.227-1.136.227-.908-.227-2.043-1.249-2.27-.908-.227-2.044.227-2.27 1.249-.228 1.248-.682 2.838.113 4.087.568.908 1.362 1.362 2.27 1.816 1.136.568 2.385.114 2.725-1.135.341-1.135.568-2.157.909-3.292.113-.568.34-1.022.567-1.59 0-.114.114-.227.114-.34.113-.228 0-.114-.114.113 0-.114.114-.114.114-.227.113-.34.227-.568.454-.795.454-.908.227-2.043-.681-2.498-.909-.113-2.157.114-2.611 1.022zM85.312 78.3c-.34 1.59-.34 3.292-.227 4.882.114 1.703.34 3.52 1.022 5.109.454.908 1.703 1.135 2.498.681.908-.568 1.135-1.59.681-2.498-.227-.34.114.454 0 0 0-.113-.114-.34-.114-.454-.113-.34-.113-.681-.227-.908 0-.114 0-.34-.113-.454-.114-.454 0 .454 0 0v-.227c0-.34-.114-.795-.114-1.136v-2.27c0-.34 0-.681.114-1.022v-.454c0-.227-.114.454 0 .227v-.227c.113-.909-.227-2.044-1.249-2.27-.908-.569-2.043 0-2.27 1.02z"
          fill="#542316"
        />
        <path
          d="M87.243 87.042c-.227-.681-.34-1.25-.454-1.93 0-.227-.114-.568-.114-.795 0-.114-.113-.681 0-.227v-.454c-.113-1.25-.113-2.498-.113-3.86 0-1.022-.909-1.93-1.817-1.817a1.793 1.793 0 00-1.816 1.817c-.114 2.724.227 5.563.908 8.174.227.908 1.362 1.59 2.27 1.249.795-.114 1.363-1.135 1.136-2.157z"
          fill="#542316"
        />
        <path
          d="M84.86 87.837c-.341-3.18-.455-6.472 0-9.65.113-1.022-.91-1.93-1.817-1.817-1.136 0-1.703.795-1.817 1.816a45.697 45.697 0 000 9.65c.114.909.795 1.93 1.817 1.817.794 0 1.93-.794 1.816-1.816z"
          fill="#542316"
        />
        <path
          d="M99.617 72.85c.227.681.568 1.25.795 1.93.341.568.568 1.25.908 1.817.114.113.114.227.227.454 0 .114.114.114.114.227 0-.34 0-.568-.114-.908 0-.114.114-.227.227-.227h0c-.113 0-.113.113-.227.113-.113 0-.113.114-.227.114h.114c-.227 0 0 .113.681.227.114.113.227.34.341.454v.568s-.114.454-.227.454l.113-.114c.114-.113.227-.227.341-.454.227-.34.227-.681 0-1.022-.114-.113-.114-.227-.227-.34l-.227-.114s.34.227.34.34c0-.113-.113-.226-.227-.34 0-.113-.113-.227-.227-.34-.227-.341-.568-.568-1.022-.568-.34 0-.681.227-.794.454-.228.34 0 0 0 0l.113-.114c.227 0 .568-.113.795-.113.227.227.567.454.795.681.227.568 0 .681 0 .454v-.113c0-.227-.114.34 0 .113 0-.113.113-.227.113-.34.114-.227.114-.568.227-.795.114-.568-.227-1.135-.795-1.249-.567-.113-1.135.227-1.248.795-.114.227-.114.567-.114.795 0 .113-.114.227.114-.114v.114c0 .113-.114.34-.114.454a1.008 1.008 0 00.795 1.362c.34.114.681 0 .908-.227.114 0 .114-.114.114-.114v-.113c.227-.227.227-.34 0-.114-.228.114-.455.228-.795.341-.227 0-.341-.114-.568-.114 0 0-.34-.227-.34-.34l.113.114c.114.113.114.227.227.34.114.114.114.227.227.227.114 0 .227.114.227.114.114 0-.227-.114-.113-.114-.341-.34-.454-.795-.227-1.135.113-.34.113-.227.113-.114l-.113.114c-.454.454-.682 1.476-.227 1.93.113.113 1.021 0 1.816-.114.908-.113 1.476-.68 1.135-1.476-.227-.454-1.816-5.222-2.043-5.563-.227-.567-.795.34-1.363.454-.34 0-.68.682-.454 1.25z"
          fill="#F4C5AE"
        />
        <path d="M98.255 72.85c.909 0 .909-1.476 0-1.476-.908 0-.908 1.476 0 1.476z" fill="#653E15" />
        <path
          d="M99.959 80.117c-.909 0-1.703-.455-2.27-1.022"
          stroke="#323232"
          strokeWidth={0.424}
          strokeMiterlimit={10}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path d="M94.396 78.754c-3.293-1.022-1.703-6.13 1.59-5.109 3.292 1.022 1.702 6.13-1.59 5.109z" fill="#F2B6AF" />
        <path
          d="M142.647 92.264c.114.682.454 1.703.454 2.157"
          stroke="#323232"
          strokeWidth={0.535}
          strokeMiterlimit={10}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M285.697 139.267c-.34-1.249-.681-2.498-1.362-3.634-.227-.34-.454-.567-.795-.681-.341-.113-.795 0-1.135.114-.681.454-.909 1.249-.454 1.93.113.227.34.568.454.795 0-.114-.114-.227-.114-.341.341.795.568 1.703.795 2.498.113.34.341.681.681.794.341.228.795.228 1.135.114.455-.114 1.022-.795.795-1.589z"
          fill="#8C593F"
        />
        <path
          d="M335.879 132.227c-16.689-4.2-24.41-2.724-30.313 1.817-4.542 3.519-3.52 14.645-3.52 14.645l-4.2-11.353-12.148 4.428s1.476 4.087 5.79 14.078c4.655 10.786 12.034 10.786 19.414 3.633 17.484-17.03 15.327-14.986 15.327-14.986"
          fill={themeContext?.palette?.illustrations[20] || '#C8B6F7'}
        />
        <path
          transform="rotate(170.288 316.811 171.292)"
          fill="url(#prefix__pattern3)"
          d="M316.811 171.292h49.1v49.1h-49.1z"
        />
        <path
          d="M294.666 238.153c-1.021 3.406 2.612 13.737 4.428 16.008 1.476 1.816-13.851.681-13.851.681s4.087-2.952-.113-15.44"
          fill={themeContext?.palette?.illustrations[80] || '#2F1D5E'}
        />
        <path
          d="M302.274 261.654c.113.795-.455 1.589-1.249 1.816-3.747.909-13.511 3.293-13.624 3.293-4.087.34-16.235.227-16.235.227-.795 0-1.363-.227-1.703-.908-.114-.227-.341-.909-.341-.909l.681-.113 32.13-4.882.113.568.228.908z"
          fill={themeContext?.palette?.illustrations[60] || '#5E3ABB'}
        />
        <path
          d="M287.173 252.231c-.908-.341-1.816.113-2.27.908-3.293 5.79-12.943 6.926-14.987 9.083-.908 1.022-.794 1.589-.794 2.725 0 .567.454.908 1.022.908 4.2 0 15.44.34 18.732-.908 1.022-.341 13.284-4.315 13.284-4.315s-.909-5.563-2.725-7.833c-.795-1.022-1.703 1.248-5.45 1.816-.908.114-3.746-.113-5.336-.568-.794-.113-.908-1.703-1.476-1.816z"
          fill={themeContext?.palette?.illustrations[40] || '#806DDB'}
        />
        <path
          d="M292.169 254.388c-4.541-.795-4.995-.681-5.904.341-1.476 1.703-4.2 3.973-6.925 5.336-1.817.908-2.838 1.703-1.93 3.179 0 0 3.406-.795 7.266-3.633 2.838-2.044 6.585-6.699 6.585-6.699M301.706 256.886c-3.86 1.589-6.472 3.292-6.018 6.017"
          stroke={themeContext?.palette?.illustrations[30] || '#AC92F2'}
          strokeWidth={0.406}
          strokeMiterlimit={10}
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeDasharray="0.9 0.9"
        />
        <path
          d="M278.999 261.995c.681 0 .681-1.022 0-1.022-.682-.114-.682 1.022 0 1.022zM281.269 260.859c.681 0 .681-1.022 0-1.022-.681-.113-.681 1.022 0 1.022zM283.427 259.383c.681 0 .681-1.022 0-1.022s-.681 1.022 0 1.022zM285.356 257.907c.682 0 .682-1.022 0-1.022-.681-.113-.681 1.022 0 1.022zM287.173 255.977c.681 0 .681-1.021 0-1.021s-.681 1.021 0 1.021z"
          fill={themeContext?.palette?.illustrations[80] || '#2F1D5E'}
        />
        <path
          d="M278.318 264.152c-1.817-.682-2.044-3.52-6.699-3.52M269.122 263.811c6.017.568 12.489.454 16.235-.227 3.747-.568 10.786-3.065 10.786-3.065"
          stroke={themeContext?.palette?.illustrations[30] || '#AC92F2'}
          strokeWidth={0.406}
          strokeMiterlimit={10}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M347.913 231.795c2.497 2.498 14.645 6.244 17.484 5.79 2.27-.454-7.267 12.148-7.267 12.148s-1.248-6.812-14.191-8.969"
          fill={themeContext?.palette?.illustrations[80] || '#2F1D5E'}
        />
        <path d="M331.565 133.703l-10.559-1.135 1.59-11.126 9.423 1.589-.454 10.672z" fill="#8C593F" />
        <path
          d="M327.251 125.869l-6.358-.681c-3.974-.908-5.109-4.314-5.336-8.628-.114-2.839 1.816-8.969 1.816-8.969 1.249-5.79 6.131-9.991 11.694-9.764 2.044.113 5.223.908 7.38 2.044 4.882 2.611 7.039 7.152 5.903 12.602 0 0-1.135 4.427-1.816 5.676-3.52 6.017-7.039 8.402-13.283 7.72z"
          fill="#8C593F"
        />
        <path
          d="M331.451 109.18c-.34.454-.908 1.59-1.021 2.384-.161 2.008-.208 3.561-.784 5.382-.302.956.562 1.987 1.524 1.706.111-.032.171-.043.259-.119.915-.786 2.496-.564 2.584.639l.033.453a1.37 1.37 0 01-.041.447l-.391 1.486a1.783 1.783 0 00-.002.903c.348 1.349 2.035 1.8 3.01.806l2.896-2.951a4.133 4.133 0 001.06-1.897l2.11-8.512a10.102 10.102 0 00-8.262-12.411l-6.722-1.039a8.236 8.236 0 00-4.195.445c-2.62 1.001-4.723 3.783-2.583 5.598.223.189.476.374.762.549 2.157 1.363 4.314 2.044 8.742 2.612 1.021.113 2.27 1.93 1.021 3.519z"
          fill="#565656"
        />
        <path
          d="M332.7 118.717c.908 1.135 2.611 1.362 3.747.454 1.135-.908 1.362-2.611.454-3.746-.908-1.136-2.157-1.249-3.292-.228-1.022.909-1.363 3.861-1.363 3.861"
          fill="#8C593F"
        />
        <path
          d="M322.141 125.529s1.93.34 4.201.567"
          stroke="#72402A"
          strokeWidth={0.428}
          strokeMiterlimit={10}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M318.508 112.359c-.454.568-.908 1.136-1.362 1.59-.454.454-.908 1.022-1.476 1.476l-.34.34c-.114 0-.114.114-.228.114.114-.227.228-.568.341-.795 0-.113-.113-.227-.113-.34l.113.113c0 .114 0 0-.113-.113 0 0 .113.113.227.113 0 0 .113.114.227.114-.114-.114-.114-.114-.114 0 .227.113 0 .113-.795 0-.113.113-.34.227-.454.34-.113.227-.113.341-.227.568 0 0 0 .454.114.454 0 0 0-.114-.114-.114-.113-.113-.113-.34-.113-.454-.114-.34.113-.794.34-1.021.114-.114.227-.227.341-.227.113 0 .227-.114.34-.114 0 0-.34.114-.454.227.114-.113.227-.113.227-.227.114-.114.227-.114.227-.227.341-.227.795-.341 1.136-.227.34.113.567.454.681.681.113.341 0-.113 0 0 0 .114 0 0-.114-.113a6.57 6.57 0 01-.794-.455c-.341.114-.682.227-1.022.455-.341.454-.227.681-.227.454v-.114V114.63c0-.227.113-.568.113-.795 0-.567.682-1.021 1.249-.908.568.114 1.022.568.908 1.249 0 .227-.113.568-.113.795v-.227V115.311c0 .681-.568 1.136-1.249 1.136a.855.855 0 01-.795-.568c0-.114-.113-.114-.113-.227v-.114c-.114-.34-.114-.34 0-.113.227.227.454.34.568.567.227 0 .34 0 .567.114 0 0 .454-.114.454-.227l-.113.113c-.114.114-.227.227-.341.227-.113.114-.227.114-.34.228-.114 0-.227.113-.341.113-.114 0 .227-.113.227-.113.454-.227.681-.682.568-1.022 0-.341-.114-.227 0-.227 0 0 0 .113.113.113.341.568.227 1.59-.454 1.93-.113.114-.908-.34-1.703-.681-.795-.34-1.249-1.135-.568-1.816.341-.341 3.406-4.428 3.747-4.769.341-.454.681.568 1.135.908.454.341.568 1.022.227 1.476z"
          fill="#8C593F"
        />
        <path d="M319.984 112.927c.908 0 .908-1.476 0-1.476s-.908 1.476 0 1.476z" fill="#663D14" />
        <path
          d="M316.124 119.398c.908.114 1.816-.227 2.498-.794"
          stroke="#323232"
          strokeWidth={0.428}
          strokeMiterlimit={10}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path d="M321.687 119.398c3.519.114 3.519-5.336.113-5.336-3.519-.113-3.633 5.336-.113 5.336z" fill="#894F3F" />
        <path
          d="M316.919 134.044c-4.996.113-8.742 9.65-9.651 17.143l-.113 28.383 30.313 3.406 2.271-29.632-2.271-13.396"
          fill={themeContext?.palette?.illustrations[20] || '#C8B6F7'}
        />
        <path
          d="M358.471 193.989c5.903 3.86 14.418 1.249 18.96-5.79 4.541-7.039 3.406-15.781-2.612-19.641l-92.642-49.5c-7.152-3.861-13.624 6.13-7.152 11.126l83.446 63.805z"
          fill={themeContext?.palette?.illustrations[50] || '#7549DA'}
        />
        <path
          d="M262.309 124.394c3.86 3.86 10.218 3.406 13.397-1.022.113-.114.227-.227.227-.454.113-.114.227-.341.34-.454 2.725-4.655.454-10.672-4.655-12.489-6.244-2.384-19.187-6.698-21.23-3.406-2.157 3.179 7.152 13.056 11.921 17.825z"
          fill="#FFBC80"
        />
        <path
          d="M264.126 110.089c.908-1.022 1.135-2.839-.454-3.747-3.406-2.044-8.061-3.179-12.034-3.52-3.293-.34-4.769 2.158-3.633 5.109 1.362 3.52 3.065 5.677 5.676 8.288 1.93 1.93 3.179 6.017 5.677 7.266.681.341 1.703.568 2.384 0 1.249-1.021 1.589-2.157 1.022-3.746-.454-1.249-.681-2.157-.909-3.293.568-.681 1.59-1.249 1.931-2.043.681-1.249.794-2.725.34-4.087.114 0 .114-.114 0-.227z"
          fill={themeContext?.palette?.illustrations[50] || '#7549EA'}
        />
        <path
          d="M320.325 160.611c.34 1.021.794 2.043 1.476 2.838.794.795 1.816 1.249 2.838 1.589.34.114.908 0 1.135-.34.114-.227.341-.341.454-.568.341-.341.454-.681.341-1.135-.114-.454-.454-.795-.795-.795-.681-.113-1.249-.341-1.93-.681.114 0 .227.113.341.113a5.282 5.282 0 01-1.136-.567l.228.227c-.114-.114-.228-.114-.228-.227l.228.227c-.228-.227-.455-.454-.682-.568-.34-.113-.681 0-.908.114-.454.34-.795 1.135-.454 1.703.568.794 1.589 1.248 2.498 1.589.454.227 1.021.341 1.476.454-.228-.681-.341-1.362-.568-2.043-.114.227-.341.34-.454.567.34-.113.794-.227 1.135-.34-.341-.114-.681-.227-.908-.341.113 0 .227.114.34.114a2.483 2.483 0 01-.908-.568l.227.227c-.227-.227-.454-.341-.567-.568l.227.227c-.341-.34-.568-.794-.682-1.248 0 .113.114.227.114.34-.114-.34-.227-.567-.341-.908-.113-.341-.227-.568-.567-.681-.227-.114-.681-.227-.909-.114-.794.114-1.248.795-1.021 1.363z"
          fill="#8C593F"
        />
        <path
          d="M313.399 159.589c.794.568 1.362 1.476 2.043 2.157a8.217 8.217 0 002.498 1.93 14.13 14.13 0 005.904 2.044v-2.385c-1.93.227-3.86.568-5.677.795-.681.114-1.249.454-1.249 1.249 0 .568.568 1.249 1.249 1.249 1.93-.227 3.86-.568 5.677-.795.681-.113 1.249-.454 1.249-1.249 0-.681-.568-1.135-1.249-1.249-1.703-.227-3.52-.908-4.996-1.816-1.703-1.135-2.611-2.838-4.314-3.974-.568-.34-1.362-.113-1.703.454-.227.568.114 1.249.568 1.59z"
          fill="#8C593F"
        />
        <path
          d="M311.015 159.929c1.022 1.022 1.93 2.157 2.952 3.179 1.135 1.022 2.043 2.157 3.179 3.065 1.93 1.476 4.2 1.59 6.585 1.363.681-.114 1.248-.454 1.248-1.249 0-.568-.567-1.249-1.248-1.249-1.931.227-3.747.227-5.336-1.022-.568-.454-.909-.908-1.363-1.362-.454-.454-1.022-.908-1.476-1.362-1.022-.909-1.93-2.044-2.838-3.066-1.022-1.135-2.725.568-1.703 1.703z"
          fill="#8C593F"
        />
        <path
          d="M311.356 161.973c1.362 1.589 2.611 3.292 4.087 4.768 1.249 1.249 2.725 1.93 4.428 2.385.908.227 1.93.34 2.952.454.908 0 2.27.113 2.952-.568.454-.454.567-1.135.227-1.703 0-.113-.227-.454-.227-.341v.341c0-.113.113-.113.113-.227.568-.795 1.135-1.476.795-2.498-.454-1.476-2.838-.908-2.384.681 0-.113.113-.227 0-.113-.114 0-.227.341-.227.341-.227.227-.341.454-.455.794-.227.454-.227.795-.227 1.249.114.454.341.681.568 1.022 0-.227-.113-.454-.113-.568v.114c0-.227.113-.454.113-.568 0 0 0 .114-.113.114l.454-.454c-.114.113-1.022 0-1.363 0-.567 0-1.249-.114-1.816-.228-1.022-.227-2.157-.454-3.179-1.135-.227-.113-.114-.113-.341-.227l-.567-.568c-.454-.454-.909-.908-1.249-1.362-.908-1.135-1.817-2.271-2.725-3.292-.454-.455-1.249-.455-1.703 0-.568.34-.454 1.021 0 1.589z"
          fill="#8C593F"
        />
        <path
          d="M313.059 158.567c1.135 1.93 2.838 3.747 4.768 4.882M311.469 160.384c1.021 1.589 3.406 3.746 4.881 4.768M321.687 162.881c.909.114 1.817.341 2.612.682"
          stroke="#323232"
          strokeWidth={0.428}
          strokeMiterlimit={10}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M336.673 146.873l4.769 17.03-17.371-2.838-1.93 10.672s10.332 2.384 19.301 3.86c8.628 1.476 13.624-4.769 11.126-14.078-1.476-5.904-4.541-19.982-8.628-24.183-3.861-3.86-9.991-7.493-25.432-7.493l2.498 5.45"
          fill={themeContext?.palette?.illustrations[20] || '#C8B6F7'}
        />
        <path
          transform="rotate(159.999 381.772 189.966)"
          fill="url(#prefix__pattern4)"
          d="M381.772 189.966h49.1v49.099h-49.1z"
        />
        <path
          d="M337.128 148.463l-.454-1.59M307.382 162.881l.114-7.607"
          stroke="#323232"
          strokeWidth={0.428}
          strokeMiterlimit={10}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M307.155 179.457s-15.554 9.082-23.274 14.418c-10.672 7.38-10.218 13.397-7.947 23.161 2.611 11.24 6.812 31.562 6.812 31.562l15.781-3.747-6.926-34.513 37.92-18.847"
          fill={themeContext?.palette?.illustrations[70] || '#462C8C'}
        />
        <path
          d="M315.783 191.605s.341 14.532.341 26.226c0 13.056 3.519 15.327 12.715 19.527 14.419 6.585 22.593 10.218 22.593 10.218l7.834-14.078-25.999-11.012 4.769-27.248-.568-12.375-30.313-3.406"
          fill={themeContext?.palette?.illustrations[70] || '#462C8C'}
        />
        <path
          d="M286.492 141.764l-.227-.227.227.227c-.227-.227-.341-.34-.341-.567 0 .113.114.227.114.34-.114-.454-.227-.795-.341-1.249-.113-.34-.113-.681-.227-.908-.113-.341-.341-.681-.681-.795-.341-.227-.795-.227-1.135-.113-.341.113-.682.34-.795.681-.227.341-.227.681-.114 1.135.114.454.227 1.022.341 1.59.113.454.227.794.454 1.249.114.113.114.227.227.34.114.227.341.454.454.568.114.113.227.227.454.34.114.114.341.114.568.114.341 0 .795-.114 1.022-.454.227-.227.454-.681.454-1.022 0-.681-.114-1.022-.454-1.249z"
          fill="#8C593F"
        />
        <path
          d="M291.373 142.559c-.113.114-.113.114 0 0 .228-.113.341-.113.455-.113.113 0 .227.113.34.113.114.114.114.114.114 0-.341-.227-.454-.341-.795-.454-.681-.227-1.249.113-1.703.568 0 0-.227.227-.114.113.114 0 .114 0 0 0h.114c.227-.113.681.114.795.341 0-.114-.114-.227-.227-.341-.454-.568-1.136-.681-1.703-.568-.227.114-.454.228-.568.341-.114.114-.341.114 0 .114.114 0 .341-.114.454 0 .114.113.114.113.114 0-.114-.114 0 0 0 .113 0 0-.114-.113-.114-.227-.113-.227-.227-.341-.34-.568-.228-.227-.682-.454-1.022-.454h-.341c-.113 0-.113 0 0 0h-.113.113.114c-.114-.113 0 0 0 0l.113.114V142.218c.341-.567.114-1.362-.454-1.703-.568-.34-1.362-.113-1.703.455-.454.794-.34 1.816.341 2.384.34.227.681.454 1.022.567.227 0 .454.114.567.114H287.059c-.227-.114-.34-.227-.567-.341-.114-.113-.114-.113 0 0 0 .114.113.114.113.227.114.228.227.341.341.455.34.34.794.567 1.249.567.34 0 .567-.113.908-.227.113 0 .34-.227.34-.227h-.113c-.227 0-.341 0-.454-.227l-.227-.113c.227.34.454.454.794.681.455.227.909.227 1.476 0l.682-.341c.113-.113.34-.227.34-.34-.34 0-.681.113-1.022.113 0 0-.113 0-.113-.113.113.113.227.227.34.227.227.113.568.227.795.227.454 0 1.022-.227 1.363-.568.454-.454.454-1.249 0-1.703-.682-.114-1.476-.227-1.931.227z"
          fill="#8C593F"
        />
        <path
          d="M288.99 143.694c.681-2.384 1.248-4.768 1.93-7.039.113-.34.227-.681.227-1.022.113-.567-.227-1.362-.795-1.476-.681-.113-1.362.228-1.476.795-.681 2.384-1.249 4.769-1.93 7.039-.114.341-.227.681-.227 1.022-.114.568.227 1.362.795 1.476.567.227 1.248-.114 1.476-.795z"
          fill="#8C593F"
        />
        <path
          d="M291.601 143.808c.454-2.044.908-3.974 1.362-6.018.113-.227.113-.567.227-.794.114-.568-.227-1.363-.795-1.476-.567-.114-1.362.227-1.476.795-.454 2.043-.908 3.973-1.362 6.017-.114.227-.114.567-.227.794-.114.568.227 1.363.795 1.476.681.227 1.362-.113 1.476-.794z"
          fill="#8C593F"
        />
        <path
          d="M291.714 138.472c0 .454-.113 1.022-.113 1.476v.34c0 .114-.114.454 0 .227v.227c0 .114 0 .341-.114.455-.113.567-.227 1.021-.34 1.589 0 .227-.114.454-.227.681 0 .114-.114.341 0 .114.113-.227.113-.114 0-.114-.114.114.227-.227.113-.227-.113.114.341-.227.114-.113-.227.113.227-.114.113-.114h0-1.022.227-.227c-.227 0-.34-.113-.567-.113-.681-.114-1.476-.341-2.157-.682-.114 0-.341-.113-.454-.113-.341-.114.227.113.113 0-.113 0-.113-.114-.227-.114l-.681-.34c-.227-.114.227.227.113.113.114.114.227.227.227.454 0 .114.114.682.114.454 0-.227-.114.341 0 .228v-.228c0-.113.113-.34.113-.454.228-.681.455-1.476.682-2.157.454-1.249.908-2.384 1.362-3.633.227-.681-.341-1.589-1.022-1.703-.794-.227-1.476.227-1.703 1.022-.454 1.135-.908 2.271-1.249 3.406-.227.795-.567 1.589-.794 2.384-.227.795-.341 1.703.34 2.384.568.454 1.249.795 1.93 1.022.682.341 1.476.568 2.271.681 1.589.341 4.201.795 4.882-1.135.454-1.249.568-2.498.681-3.86.114-.681.227-1.476.227-2.157.114-.795-.681-1.476-1.362-1.363-.681 0-1.249.568-1.363 1.363z"
          fill="#8C593F"
        />
        <path
          d="M288.536 134.952l-1.703 6.585M291.374 135.406l-1.93 7.153M293.303 136.996s-.795 3.746-1.476 6.244M285.129 138.131c-.113.681-.454 1.703-.454 2.157"
          stroke="#323232"
          strokeWidth={0.535}
          strokeMiterlimit={10}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M332.019 173.78l-9.878-2.043.341-1.93M315.897 198.303l-.113-6.698-1.703-1.363"
          stroke="#323232"
          strokeWidth={0.428}
          strokeMiterlimit={10}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M373.231 239.061c.795.341 1.249 1.135.908 2.044-.908 3.179-2.951 8.855-4.2 12.148-1.476 3.746-6.244 12.034-6.244 12.034-.455.908-1.931 1.817-2.839 1.817-1.022 0-3.179.113-5.222 0-.568 0-.795-.682-.795-1.136l7.039-.795 6.131-12.147 3.746-14.419.568.227.908.227z"
          fill={themeContext?.palette?.illustrations[60] || '#5E3ABB'}
        />
        <path
          d="M357.45 247.122c-.681.568-.794 1.589-.34 2.498 2.043 3.633 2.725 9.196.795 12.602-.795 1.362-2.385 1.021-2.725 2.384-.227.908-.114 1.589-.114 1.589s3.974.227 6.017-.113c.682-.114 1.817-.908 2.271-1.59 2.044-3.633 4.428-8.628 5.79-12.148 1.136-2.838 3.406-13.623 3.406-13.623s-5.222-2.044-8.174-1.703c-1.249.113.227 2.043-1.249 5.676-.341.908-2.044 3.179-3.292 4.314-.568.341-2.044-.227-2.385.114z"
          fill={themeContext?.palette?.illustrations[40] || '#806DDB'}
        />
        <path
          d="M361.879 244.057c-2.952 3.519-3.179 3.973-2.725 5.222.681 2.157 1.249 5.677 1.022 8.742-.227 2.044-1.817 4.655.34 4.655 0 0 2.498-4.768 2.044-9.537-.341-3.519-2.384-9.082-2.384-9.082M368.917 237.017c-.681 4.087-.454 7.153 2.157 8.175"
          stroke={themeContext?.palette?.illustrations[30] || '#AC92F2'}
          strokeWidth={0.406}
          strokeMiterlimit={10}
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeDasharray="0.9 0.9"
        />
        <path
          d="M361.197 259.724c.341-.454-.454-1.022-.795-.454-.34.454.454.908.795.454zM361.651 256.999c.34-.568-.568-1.135-.908-.568-.341.568.567 1.136.908.568zM361.765 254.274c.34-.567-.568-1.135-.908-.567-.341.567.567 1.135.908.567zM361.424 251.777c.34-.568-.568-1.136-.908-.568-.341.681.567 1.249.908.568zM360.743 249.052c.341-.568-.568-1.136-.908-.568-.454.568.454 1.135.908.568z"
          fill={themeContext?.palette?.illustrations[80] || '#2F1D5E'}
        />
        <path
          d="M363.808 261.2c-1.816 2.952-3.519.227-7.607 1.703M354.726 265.287c1.93 0 5.676.227 6.471-.567 2.725-2.385 5.223-9.31 6.017-11.467 1.363-3.52 2.157-9.196 2.157-9.196"
          stroke={themeContext?.palette?.illustrations[30] || '#AC92F2'}
          strokeWidth={0.406}
          strokeMiterlimit={10}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          transform="rotate(-99.054 264.765 229.633)"
          fill="url(#prefix__pattern5)"
          d="M264.765 229.633h64.228v64.228h-64.228z"
        />
        <path
          transform="matrix(-.04264 -.0186 -.0186 .04264 399 199.542)"
          fill="url(#prefix__pattern6)"
          d="M0 0h1226.15v1226.15H0z"
        />
        <defs>
          <pattern id="prefix__pattern0" patternContentUnits="objectBoundingBox" width={1} height={1}>
            <use xlinkHref="#prefix__image0" />
          </pattern>
          <pattern id="prefix__pattern1" patternContentUnits="objectBoundingBox" width={1} height={1}>
            <use xlinkHref="#prefix__image0" />
          </pattern>
          <pattern id="prefix__pattern2" patternContentUnits="objectBoundingBox" width={1} height={1}>
            <use xlinkHref="#prefix__image0" />
          </pattern>
          <pattern id="prefix__pattern3" patternContentUnits="objectBoundingBox" width={1} height={1}>
            <use xlinkHref="#prefix__image0" />
          </pattern>
          <pattern id="prefix__pattern4" patternContentUnits="objectBoundingBox" width={1} height={1}>
            <use xlinkHref="#prefix__image0" />
          </pattern>
          <pattern id="prefix__pattern5" patternContentUnits="objectBoundingBox" width={1} height={1}>
            <use xlinkHref="#prefix__image0" />
          </pattern>
          <pattern id="prefix__pattern6" patternContentUnits="objectBoundingBox" width={1} height={1}>
            <use xlinkHref="#prefix__image0" />
          </pattern>
          <image
            id="prefix__image0"
            xlinkHref="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAABDgAAAQ4CAYAAADsEGyPAAAACXBIWXMAAAsSAAALEgHS3X78AAAA"
          />
        </defs>
      </svg>
    </Holder>
  )
}

export default PaintbrushIcon

const phoneSizeRatio = 111 / defaultSize

const Phone = ({ color = Colors.N100, size = 111 }: BaseIconProps) => {
  return (
    <svg
      width={size * phoneSizeRatio}
      height={size * phoneSizeRatio}
      style={{ position: 'relative', left: size / 25 }}
      viewBox="0 0 111 111"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M104.913 78.1351C105.01 77.9426 104.972 77.7101 104.82 77.5579L90.6152 63.3534C90.453 63.1912 90.2012 63.1602 90.0044 63.2782L79.9796 69.2931C79.873 69.3571 79.7536 69.3805 79.6326 69.3517C78.1898 69.0085 68.3215 66.3204 56.6582 54.6571C44.9949 42.9938 42.3069 33.1256 41.9636 31.6828C41.9348 31.5618 41.9583 31.4424 42.0223 31.3357L48.0371 21.3109C48.1552 21.1142 48.1242 20.8623 47.9619 20.7001L33.7574 6.49557C33.6052 6.34339 33.3727 6.30566 33.1802 6.40191L16.9153 14.5344C16.7582 14.6129 16.6547 14.7594 16.6469 14.9348C16.5168 17.8533 16.1532 43.6229 41.9228 69.3925C67.6924 95.1622 93.4621 94.7986 96.3805 94.6685C96.556 94.6606 96.7024 94.5571 96.781 94.4L104.913 78.1351Z"
        stroke={color}
        strokeWidth="2.5"
      />
    </svg>
  )
}

const glassSizeRatio = 75 / defaultSize
const glassIconRatio = 81 / 75

type GlassProps = {
  icon: ({ color }: { color: Colors; size?: number }) => JSX.Element
  iconSize?: number
  iconPosition?: Coords
} & BaseIconProps

const IconHolder = styled.div<{ $iconPosition: Coords }>`
  position: absolute;
  width: 100%;
  text-align: center;
  > svg {
    position: relative;
    left: ${({ $iconPosition }) => $iconPosition.left}px;
    top: ${({ $iconPosition }) => $iconPosition.top}px;
  }
`

const Glass = ({
  color = Colors.N100,
  size = 75,
  icon,
  iconSize = 30,
  iconPosition = { left: 0, top: 0 }
}: GlassProps) => {
  const Icon = icon
  return (
    <>
      <IconHolder $iconPosition={iconPosition}>
        <Icon color={color as Colors} size={iconSize} />
      </IconHolder>
      <svg
        width={size * glassSizeRatio}
        height={size * glassSizeRatio * glassIconRatio}
        style={{ position: 'relative', left: size / 50, top: size / 50 }}
        viewBox="0 0 75 81"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle cx="29.2134" cy="29.3457" r="27.6724" stroke={color} strokeWidth="2.78947" />
        <path d="M54.1182 59.7842L73.4888 79.1548" stroke={color} strokeWidth="2.78947" strokeLinecap="round" />
      </svg>
    </>
  )
}
